import React, { FC, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Event } from 'betweb-openapi-axios'
import { getFormattedDate } from 'astra-core/utils/date/getFormattedDate'
import {
  getEventListV2,
  getIsEventListFilterUsed,
  getIsNextPageLoading
} from 'astra-core/containers/EventsProvider'
import { EventsApiGetEventsV2Request } from 'astra-core/containers/EventsProvider/types'
import {
  useFilterBySearch,
  useResetFilters
} from 'astra-core/containers/EventsProvider/hooks/list-filters'
import { useVirtualScrollActions } from 'astra-core/containers/EventsProvider/hooks/useVirtualScrollActions'
import { prepareListItems } from 'astra-core/utils/eventsList'

import { LoaderSpinner } from 'shared/ui/LoaderSpinner'
import { LineParams } from 'pages/page-line/LineContainer/types'
import { useAppSelector } from 'store'
import { ClearResultSearch } from 'shared/ui/ClearResult'
import { EmptyPageFilter } from 'shared/ui/EmptyPageFilter'
import { OutcomesFetchType } from 'entities/outcomes/model/types'
import { VirtualizedEventsListWithAutoSize as VirtualizedEventsList } from 'widgets/VirtualizedEventsList'

import { LINE_EVENTS_LIST_KEY, LINE_PAGE_SIZE } from '../../model/constants'

const outcomesFetchType: OutcomesFetchType = {
  kind: 'STREAM'
}

export const EventsTable: FC = () => {
  const { tournamentId } = useParams<LineParams>()

  const events = useAppSelector((state) =>
    getEventListV2(state, LINE_EVENTS_LIST_KEY)
  )

  const { listItems, pageNumbersOfListItems } = useMemo(() => {
    return prepareListItems(events, LINE_PAGE_SIZE, {
      groups: ['tournament', 'date'],
      dateTransform: (eventDate: Event['eventDate']) =>
        getFormattedDate({ date: eventDate, format: 'YYYY-MM-DD' })
    })
  }, [events])

  const { value: searchText } = useFilterBySearch(LINE_EVENTS_LIST_KEY)

  const { onResetFilters } = useResetFilters(LINE_EVENTS_LIST_KEY)

  const isLoading = useAppSelector((state) =>
    getIsNextPageLoading(state, LINE_EVENTS_LIST_KEY)
  )

  const isFilterUsed = useAppSelector((state) =>
    getIsEventListFilterUsed(state, LINE_EVENTS_LIST_KEY)
  )

  const request: EventsApiGetEventsV2Request = useMemo(
    () => ({
      tournamentIds: [+tournamentId],
      scheduledFromNow: true
    }),
    [tournamentId]
  )

  const { hasNextPage, isNextPageLoading, loadNextPage, scrollUp } =
    useVirtualScrollActions({
      listKey: LINE_EVENTS_LIST_KEY,
      request,
      paramsExist: !!tournamentId
    })

  if (!events.length) {
    if (isLoading) {
      return <LoaderSpinner />
    }

    if (searchText) {
      return <ClearResultSearch />
    }

    if (isFilterUsed) {
      return <EmptyPageFilter onButtonClickResetFilter={onResetFilters} />
    }
    return null
  }

  return (
    <VirtualizedEventsList
      hasNextPage={hasNextPage}
      isNextPageLoading={isNextPageLoading}
      listItems={listItems}
      listKey={LINE_EVENTS_LIST_KEY}
      loadNextPage={loadNextPage}
      outcomesFetchType={outcomesFetchType}
      pageNumbersOfListItems={pageNumbersOfListItems}
      scrollUp={scrollUp}
      view="line"
    />
  )
}
